import React, { useState } from 'react';
import { Col, Container, Row, Collapse } from 'reactstrap';
import classnames from 'classnames';

/**
 * The questions someone actually has before signing up.
 *
 * Rewritten from a set that answered "how good are your predictions" — a
 * question this product no longer asks anyone to care about. These answer the
 * real ones: whose AI is it, what does it cost, can I use my own method, and
 * what happens to what I write down.
 *
 * Driven by a list rather than eight hand-wired useState pairs, so adding a
 * question is one entry.
 */

const FAQS = [
    {
        q: 'Do I need to buy an AI subscription from you?',
        a: `No. You use whatever you already pay for — ChatGPT, Claude, Gemini, Grok,
            DeepSeek, Kimi, Perplexity, or something else entirely. We build the prompt
            and keep the record; the thinking happens wherever you already do it.`,
    },
    {
        q: 'How accurate are the forecasts?',
        a: `That depends on your AI, not on us, and we will not put a number on it in
            marketing. What we will do is measure yours honestly and show you the result:
            how often the close landed inside the stated range, and whether the confidence
            numbers hold up. If your model is not good at this, your scorecard will say so.`,
    },
    {
        q: 'Can I use my own prompt instead of your templates?',
        a: `Yes. The templates are a starting point. The only requirement is that the
            answer comes back in the expected format — the price the model found, a price
            range, a confidence, and a direction per horizon. Anything matching that is
            parsed, stored and scored the same way, and sits beside the rest of your
            history. Keep the price in: we never tell the model what the asset is trading
            at, and how close its own figure lands is part of what gets scored.`,
    },
    {
        q: 'What is free, and what needs a subscription?',
        a: `A free membership runs 15 research prompts a month, keeps its full history and
            scorecard, and reads the four standing experiments once they settle. $9.99 a
            month raises the allowance to 100, lets you keep up to ten private research
            strategies instead of one, lets you follow the standing experiments before
            they settle, and opens the experiment results subscribers have shared with
            each other. More lands on that side over time.
            What does not change is the research itself — same prompt, same record, same
            scoring. You are not paying for a better answer. Billed monthly, cancel at any time, and you keep the month you
            have paid for.`,
    },
    {
        q: 'Who can see my prompts?',
        a: `You, and nobody else. A prompt belongs to whoever made it, on every plan:
            another member cannot read yours, and paying does not change that. Sharing a
            forecast shares its result — the range, the confidence, the reasoning and how
            it scored — never the prompt behind it. The same holds for our own standing
            experiments: their prompts are our research and are not published; each run
            shows its prompt version and fingerprint instead, so you can tell the prompt
            was not changed after the results.`,
    },
    {
        q: 'How does a forecast get scored?',
        a: `You settle it. When the horizon closes, enter what the asset was worth when you
            asked and what it closed at, and every model that answered that same question is
            settled at once — they shared a window, so they share an outcome. Optionally add
            the window's high and low. Every forecast generates a TradingView script that
            reads all four figures off the chart for you, which is both easier and more
            accurate than looking them up. Everything after that is arithmetic we do.`,
    },
    {
        q: 'Can I edit or delete a forecast after saving it?',
        a: `You can delete one that has not been settled yet — for a mis-selected model or
            a wrong paste. You cannot edit one, and you cannot delete one that has already
            been settled. A track record you can revise after the fact is not a track
            record, and every number on your scorecard is computed from these rows.`,
    },
    {
        q: 'Is my research private?',
        a: `Private by default. Sharing is an explicit act, one forecast at a time. Shared
            research shows only your display name.`,
    },
    {
        q: 'Which symbols can I research?',
        a: `Any listed stock, ETF or index, and any major crypto pair. Your AI does the
            looking, so you are not limited to a list we maintain. Search by ticker or
            company name.`,
    },
    {
        q: 'Is this investment advice?',
        a: `No. ForecastNest is a research and record-keeping tool. Nothing here is a
            recommendation to buy or sell anything, and the forecasts are produced by your
            own AI rather than by us. Treat it as a notebook that checks its own arithmetic.`,
    },
];

const Faqs = () => {
    const [open, setOpen] = useState(0);
    const toggle = (i) => setOpen(open === i ? -1 : i);

    // Two balanced columns on desktop, a single stack on narrow screens.
    const half = Math.ceil(FAQS.length / 2);
    const columns = [FAQS.slice(0, half), FAQS.slice(half)];

    return (
        <React.Fragment>
            <section className="section" id="faqs">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="text-center mb-5">
                                <h1 className="mb-3 ff-secondary fw-semibold lh-base">
                                    Questions worth asking first
                                </h1>
                                <p className="text-muted">
                                    Still unsure about something? The contact form below reaches us
                                    directly.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-lg-5 gy-4">
                        {columns.map((column, colIndex) => (
                            <Col lg={6} key={colIndex}>
                                <div className="accordion custom-accordionwithicon accordion-border-box">
                                    {column.map((faq, rowIndex) => {
                                        const index = colIndex * half + rowIndex;
                                        const isOpen = open === index;
                                        return (
                                            <div className="accordion-item" key={faq.q}>
                                                <h2 className="accordion-header">
                                                    <button
                                                        type="button"
                                                        className={classnames('accordion-button', { collapsed: !isOpen })}
                                                        onClick={() => toggle(index)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {faq.q}
                                                    </button>
                                                </h2>
                                                <Collapse isOpen={isOpen} className="accordion-collapse">
                                                    <div className="accordion-body">{faq.a}</div>
                                                </Collapse>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default Faqs;
